import { Loader2Icon } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { useMutation } from '@apollo/client';
import { zodResolver } from '@hookform/resolvers/zod';
import { toast } from 'sonner';
import { z } from 'zod';

import {
  Button,
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  Input,
  ModalContent,
  ModalFooter,
  RadioCheckboxItem,
  RadioGroup,
  useErrorHandler,
} from '@sandtech-hub-monorepo/ui-kit';
import {
  CreateOrUpdateProofOfCompletionDocument,
  GetViewerLearningDetailsDocument,
  ProofOfCompletionType,
  ExamStatementType,
} from '@sandtech-hub-monorepo/ehub-client';

import { ReactComponent as CertificateIcon } from '../../assets/Icons/certificate.svg';
import { ProofOfCompletionModalProps } from './Banner';
import {
  ProofOfCompletionGenericFormFields,
  proofOfCompletionGenericFormSchema,
} from './GenericFormFields';
import {
  getExamLocationOptions,
  getLabelByExamStatement,
  getModalSubtitleByProofOfCompletionType,
} from './util';
import { useGetMaybeViewer } from '@sandtech-hub-monorepo/ui-auth-kit';

const formSchema = proofOfCompletionGenericFormSchema
  .extend({
    examScore: z.union([z.number().optional(), z.nan()]),
    validationNumber: z.string().optional(),
  })
  .superRefine((data, ctx) => {
    if (data.examStatement !== ExamStatementType.DidNotCompleteExam) {
      if (
        data.examScore === undefined ||
        isNaN(data.examScore as number) ||
        (!isNaN(data.examScore) && data.examScore < 700)
      ) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['examScore'],
        });
      }

      if (data.validationNumber === undefined || data.validationNumber === '') {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['validationNumber'],
        });
      }
    }
  });

export const AWSCloudPractitionerModal = ({
  close,
  classEnrollmentId,
}: ProofOfCompletionModalProps) => {
  const { viewer } = useGetMaybeViewer();
  const [createProofOfCompletion, { loading }] = useMutation(
    CreateOrUpdateProofOfCompletionDocument
  );

  const { handleGeneralAppError } = useErrorHandler();

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      firstName: viewer?.firstName || '',
      lastName: viewer?.lastName || '',
      emailUsedInExam: viewer?.primaryEmail,
      examLocation: undefined,
      validationNumber: undefined,
      examScore: undefined,
    },
  });

  const onSubmit = (values: z.infer<typeof formSchema>) => {
    createProofOfCompletion({
      variables: {
        input: {
          classEnrollmentId,
          firstName: values.firstName,
          lastName: values.lastName,
          email: values.emailUsedInExam,
          examLocation: values.examLocation,
          examScore: Number(values.examScore),
          examStatement: values.examStatement,
          examValidationNumber: values.validationNumber,
        },
      },
      refetchQueries: [{ query: GetViewerLearningDetailsDocument }],
      onError: () => handleGeneralAppError(),
      onCompleted: () => {
        toast.info('Your Proof of completion was successfully submitted!');
        close();
      },
    });
  };

  return (
    <ModalContent>
      <div className="flex flex-col gap-8 w-[80%] mx-auto">
        <div className="flex flex-col gap-4 items-center justify-center mx-auto">
          <div className="flex items-center justify-center w-24 h-24 bg-background-quaternary rounded-full">
            <CertificateIcon />
          </div>
          <h2 className="font-semibold text-2xl text-primary">
            Proof of Completion
          </h2>
          <p className="font-medium text-center">
            {getModalSubtitleByProofOfCompletionType(
              ProofOfCompletionType.AwsCloudComputing
            )}
          </p>
        </div>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)}>
            <div className="flex flex-col gap-6">
              <ProofOfCompletionGenericFormFields
                proofOfCompletionType={ProofOfCompletionType.AwsCloudComputing}
              />

              <FormField
                control={form.control}
                name="examStatement"
                render={({ field }) => (
                  <FormItem className="space-y-2">
                    <FormLabel>
                      Which of these statements applies to you with regard to
                      the AWS Cloud Practitioner exam?*
                    </FormLabel>
                    <FormControl>
                      <RadioGroup
                        onValueChange={field.onChange}
                        defaultValue={field.value}
                      >
                        <div className="flex flex-col gap-4">
                          {Object.values(ExamStatementType).map(
                            (statementType) => (
                              <RadioCheckboxItem
                                id={statementType}
                                value={statementType}
                                label={getLabelByExamStatement(
                                  statementType,
                                  ProofOfCompletionType.AwsCloudComputing
                                )}
                              />
                            )
                          )}
                        </div>
                      </RadioGroup>
                    </FormControl>
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="examLocation"
                render={({ field }) => (
                  <FormItem className="space-y-2">
                    <FormLabel>Where did you take your exam?*</FormLabel>
                    <FormControl>
                      <RadioGroup
                        onValueChange={field.onChange}
                        defaultValue={field.value}
                      >
                        <div className="flex flex-col gap-4">
                          {getExamLocationOptions(
                            ProofOfCompletionType.AwsCloudComputing
                          ).map((option) => (
                            <RadioCheckboxItem
                              value={option.value}
                              id={option.value}
                              label={option.label}
                            />
                          ))}
                        </div>
                      </RadioGroup>
                    </FormControl>
                  </FormItem>
                )}
              />

              {form.watch('examStatement') &&
                form.watch('examStatement') !==
                  ExamStatementType.DidNotCompleteExam && (
                  <>
                    <FormField
                      control={form.control}
                      name="examScore"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel htmlFor="examScore" className="text-sm">
                            Your exam score*
                          </FormLabel>
                          <FormControl>
                            <Input
                              {...field}
                              type="number"
                              placeholder="e.g. 720"
                              onChange={(e) =>
                                form.setValue(
                                  'examScore',
                                  e.target.valueAsNumber
                                )
                              }
                            />
                          </FormControl>
                          <FormDescription className="text-xs">
                            Please note, a score of 700 or above is required
                          </FormDescription>
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="validationNumber"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel
                            htmlFor="validationNumber"
                            className="text-sm"
                          >
                            Validation number*
                          </FormLabel>
                          <FormControl>
                            <Input {...field} />
                          </FormControl>
                          <FormDescription className="text-xs">
                            This can be found on your certificate
                          </FormDescription>
                        </FormItem>
                      )}
                    />
                  </>
                )}
            </div>
            <ModalFooter className="sm:justify-center">
              <Button variant="outline" onClick={close}>
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={!form.formState.isDirty || loading}
              >
                {loading ? <Loader2Icon className="animate-spin" /> : 'Submit'}
              </Button>
            </ModalFooter>
          </form>
        </Form>
      </div>
    </ModalContent>
  );
};
